import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { GstVerificationDetails, GstVerificationJobDetail, GstVerificationJobStatus, GstVerificationJobSummary } from "../../features/admin/types";
import { formatISTDateTime } from "../../utils/dateUtils";
import ConfirmModal from "../../components/admin/ConfirmModal";
import { useAlertModal } from "../../components/admin/AlertModal";
import Spinner from "../../components/ui/Spinner";
import RefreshButton from "../../components/admin/RefreshButton";
import { useBodyScrollLock } from "../../hooks/useBodyScrollLock";
import { useAdminToast } from "../../context/AdminToastContext";

interface GstVerifyPopupProps {
  job: GstVerificationJobSummary;
  onClose: () => void;
  onConfirmed: () => void;
}

/** "Verify" flow popup: live-fetches the GST portal details for this job's
 *  GSTIN (no persistence) so the admin sees exactly what will be written
 *  before committing, then a confirm step actually re-verifies + upserts. */
const GstVerifyPopup: React.FC<GstVerifyPopupProps> = ({ job, onClose, onConfirmed }) => {
  useBodyScrollLock();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState<GstVerificationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showError } = useAdminToast();
  useEffect(() => { if (error) showError(error); }, [error, showError]);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminService
      .fetchGstVerificationJobDetails(job.job_id)
      .then((d) => { if (!cancelled) setDetails(d); })
      .catch((err) => { if (!cancelled) setError(err?.response?.data?.detail || "Failed to fetch GST details from the portal."); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [job.job_id]);

  const handleConfirm = async () => {
    setConfirming(true);
    setError(null);
    try {
      await adminService.approveGstVerificationJob(job.job_id);
      onConfirmed();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to verify job.");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Verify GSTIN</h2>
            <p className="text-sm text-gray-400 mt-0.5">{job.gstin}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
        </div>

        {loading ? (
          <div className="py-16 flex justify-center"><Spinner /></div>
        ) : error && !details ? (
          <div className="text-sm text-red-400 bg-red-500/10 rounded-md p-3 break-words">{error}</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div><div className="text-xs text-gray-500">Legal name</div><div className="text-gray-200">{details?.legal_name || "—"}</div></div>
              <div><div className="text-xs text-gray-500">Trade name</div><div className="text-gray-200">{details?.trade_name || "—"}</div></div>
              <div><div className="text-xs text-gray-500">Portal status</div><div className="text-gray-200">{details?.status || "—"}</div></div>
              <div><div className="text-xs text-gray-500">Pincode</div><div className="text-gray-200">{details?.pincode || "—"}</div></div>
              <div><div className="text-xs text-gray-500">City</div><div className="text-gray-200">{details?.city || "—"}</div></div>
              <div><div className="text-xs text-gray-500">State</div><div className="text-gray-200">{details?.state || "—"}</div></div>
              <div><div className="text-xs text-gray-500">Country</div><div className="text-gray-200">{details?.country || "—"}</div></div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">Address</div>
              <div className="text-gray-200 text-xs break-words">{details?.address || "—"}</div>
            </div>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 rounded-md p-3 break-words">{error}</div>
            )}
            <p className="text-xs text-gray-500 pt-1">
              Confirming re-verifies this GSTIN against the live GST portal and upserts this data onto the seller's profile.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white rounded-md transition-colors">Cancel</button>
              <button
                onClick={handleConfirm}
                disabled={confirming}
                className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-500 text-white rounded-md transition-colors disabled:opacity-50"
              >
                {confirming ? "Verifying…" : "Confirm & Verify"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface GstJobDetailModalProps {
  job: GstVerificationJobDetail | null;
  loading: boolean;
  onClose: () => void;
}

/** Surfaces what the list view truncates or omits entirely: full root-cause
 * error history, when the first failure happened, and whether an
 * "in_progress" job is actually wedged past the worker's staleness
 * threshold. */
const GstJobDetailModal: React.FC<GstJobDetailModalProps> = ({ job, loading, onClose }) => {
  useBodyScrollLock();
  if (!loading && !job) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg shadow-2xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {loading || !job ? (
          <div className="py-16 flex justify-center"><Spinner /></div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-white">GST Job Detail</h2>
                <p className="text-sm text-gray-400 mt-0.5">{job.gstin}</p>
                <p className="text-sm text-gray-300 mt-0.5">
                  {job.user_name || "Unknown user"}
                  {job.user_email && <span className="text-gray-500"> · {job.user_email}</span>}
                </p>
                <p className="text-xs text-gray-600 mt-0.5">{job.user_id}</p>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-white text-sm">Close</button>
            </div>

            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor(job.status)}`}>{job.status}</span>
                {job.stuck && (
                  <span className="px-2 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-400">
                    Stuck since {formatISTDateTime(job.updated_at)}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                <div>Created<div className="text-gray-200">{formatISTDateTime(job.created_at)}</div></div>
                <div>Updated<div className="text-gray-200">{formatISTDateTime(job.updated_at)}</div></div>
                {job.first_error_at && (
                  <div>First error<div className="text-gray-200">{formatISTDateTime(job.first_error_at)}</div></div>
                )}
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Current error</div>
                <div className="text-gray-200 text-xs break-words">{job.error || "—"}</div>
              </div>

              <div>
                <div className="text-xs text-gray-500 mb-1">Error history</div>
                {job.error_history && job.error_history.length > 0 ? (
                  <ul className="space-y-2">
                    {job.error_history.map((entry, idx) => (
                      <li key={idx} className="text-xs bg-gray-800/60 rounded-md p-2">
                        <div className="text-gray-500">{formatISTDateTime(entry.at)}</div>
                        <div className="text-gray-200 break-words">{entry.message}</div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-xs text-gray-600">No prior errors recorded.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Mirrors the backend's real six statuses (see
// backend/app/modules/admin/gst_verification_routes.py /
// backend/app/modules/gst_verification/repository.py) — there is no
// "verified"/"approved" status the backend ever emits.
const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: "all", label: "All" },
  { value: "queued", label: "Queued" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "stopped_for_review", label: "Stopped for Review" },
  { value: "rejected", label: "Rejected" },
];

const STOPPABLE_STATUSES: GstVerificationJobStatus[] = ["queued", "in_progress"];
const REJECTABLE_STATUSES: GstVerificationJobStatus[] = ["stopped_for_review", "failed"];
// "Verify" (live-fetch + re-verify) is available for completed jobs too —
// e.g. to re-check/refresh one, or recover it after it's been superseded.
const VERIFY_BUTTON_STATUSES: GstVerificationJobStatus[] = ["stopped_for_review", "failed", "completed"];

function statusColor(status: string): string {
  const map: Record<string, string> = {
    queued: "bg-gray-500/10 text-gray-400",
    in_progress: "bg-amber-500/10 text-amber-400",
    completed: "bg-green-500/10 text-green-400",
    failed: "bg-red-500/10 text-red-400",
    stopped_for_review: "bg-orange-500/10 text-orange-400",
    rejected: "bg-red-500/10 text-red-400",
  };
  return map[status] || "bg-gray-500/10 text-gray-400";
}

type ConfirmAction = { type: "stop" | "reject"; job: GstVerificationJobSummary };

const AdminGstVerificationPage: React.FC = () => {
  const [jobs, setJobs] = useState<GstVerificationJobSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [detailJob, setDetailJob] = useState<GstVerificationJobDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [verifyJob, setVerifyJob] = useState<GstVerificationJobSummary | null>(null);
  const { alertModal, showAlert } = useAlertModal();

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const items = await adminService.getGstVerificationJobs(statusFilter === "all" ? undefined : statusFilter);
      setJobs(items);
    } catch (err) {
      console.error(err);
      showAlert("Failed to load GST verification jobs.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  const handleStop = async (job: GstVerificationJobSummary) => {
    setConfirmBusy(true);
    try {
      await adminService.stopGstVerificationJob(job.job_id);
      setConfirmAction(null);
      fetchJobs();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to stop job.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleReject = async (job: GstVerificationJobSummary) => {
    setConfirmBusy(true);
    try {
      await adminService.rejectGstVerificationJob(job.job_id);
      setConfirmAction(null);
      fetchJobs();
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to reject job.");
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleViewDetail = async (job: GstVerificationJobSummary) => {
    setDetailLoading(true);
    setDetailJob(null);
    try {
      const detail = await adminService.getGstVerificationJob(job.job_id);
      setDetailJob(detail);
    } catch (err: any) {
      console.error(err);
      showAlert(err?.response?.data?.detail || "Failed to load job detail.");
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <>
      <div>
        <AdminPageHeader
          title="GST Verification"
          subtitle="Async GST verification job queue — stop stuck jobs, verify (live portal fetch) or reject ones flagged for manual review."
          actions={<RefreshButton onRefresh={fetchJobs} />}
        />

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === s.value ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">GSTIN</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">User</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Status</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Error</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Created</th>
                  <th className="text-left py-3 px-4 text-gray-500 font-medium">Updated</th>
                  <th className="text-right py-3 px-4 text-gray-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="py-20 text-center text-gray-500"><Spinner /></td></tr>
                ) : !jobs || jobs.length === 0 ? (
                  <tr><td colSpan={7} className="py-20 text-center text-gray-500">No GST verification jobs found</td></tr>
                ) : (
                  jobs.map((job) => {
                    const canStop = STOPPABLE_STATUSES.includes(job.status);
                    const canVerify = VERIFY_BUTTON_STATUSES.includes(job.status);
                    const canReject = REJECTABLE_STATUSES.includes(job.status);
                    return (
                      <tr
                        key={job.job_id}
                        onClick={() => handleViewDetail(job)}
                        className="cursor-pointer border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors"
                      >
                        <td className="py-3 px-4 text-white font-medium">{job.gstin}</td>
                        <td className="py-3 px-4 text-gray-400">
                          <div className="text-gray-200">{job.user_name || "Unknown user"}</div>
                          {job.user_email && <div className="text-xs text-gray-500">{job.user_email}</div>}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <span className={`px-2 py-1 rounded-md text-xs font-medium ${statusColor(job.status)}`}>
                              {job.status}
                            </span>
                            {job.stuck && (
                              <span className="px-2 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-400">
                                Stuck
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs max-w-xs truncate" title={job.error || undefined}>
                          {job.error || "—"}
                        </td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatISTDateTime(job.created_at)}</td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{formatISTDateTime(job.updated_at)}</td>
                        <td className="py-3 px-4 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleViewDetail(job)}
                            className="px-2 py-1 text-xs text-indigo-400 hover:bg-indigo-500/10 rounded-md transition-colors mr-1"
                          >
                            Details
                          </button>
                          {canStop && (
                            <button
                              onClick={() => setConfirmAction({ type: "stop", job })}
                              className="px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded-md transition-colors mr-1"
                            >
                              Stop
                            </button>
                          )}
                          {canVerify && (
                            <button
                              onClick={() => setVerifyJob(job)}
                              className="px-2 py-1 text-xs text-green-400 hover:bg-green-500/10 rounded-md transition-colors mr-1"
                            >
                              Verify
                            </button>
                          )}
                          {canReject && (
                            <button
                              onClick={() => setConfirmAction({ type: "reject", job })}
                              className="px-2 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                            >
                              Reject
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {confirmAction && (
        <ConfirmModal
          title={confirmAction.type === "stop" ? "Stop Verification Job" : "Reject Verification Job"}
          description={
            confirmAction.type === "stop"
              ? `GSTIN ${confirmAction.job.gstin} will be pulled out of the queue and marked for manual review.`
              : `GSTIN ${confirmAction.job.gstin} will be rejected — the user's GST will not be verified.`
          }
          confirmLabel={confirmAction.type === "stop" ? "Stop Job" : "Reject Job"}
          confirmClass={confirmAction.type === "stop" ? "bg-amber-600 hover:bg-amber-500" : "bg-red-600 hover:bg-red-500"}
          irreversible={confirmAction.type === "reject"}
          busy={confirmBusy}
          busyLabel="Working…"
          onConfirm={() => (confirmAction.type === "stop" ? handleStop(confirmAction.job) : handleReject(confirmAction.job))}
          onCancel={() => setConfirmAction(null)}
        />
      )}
      {(detailLoading || detailJob) && (
        <GstJobDetailModal
          job={detailJob}
          loading={detailLoading}
          onClose={() => {
            setDetailJob(null);
            setDetailLoading(false);
          }}
        />
      )}
      {verifyJob && (
        <GstVerifyPopup
          job={verifyJob}
          onClose={() => setVerifyJob(null)}
          onConfirmed={() => {
            setVerifyJob(null);
            fetchJobs();
          }}
        />
      )}
      {alertModal}
    </>
  );
};

export default AdminGstVerificationPage;
